import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Save, History, Clock, MoreHorizontal, Pencil, Trash2, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useBreakdownVersions, useBreakdownVersion, useUpdateBreakdownVersion, useDeleteBreakdownVersion } from '@/hooks/useBreakdown';
import type { BreakdownVersionWithUser, JobBreakdown } from '@jobos/shared';
import { formatCurrency } from '@/lib/utils';
import { SaveVersionDialog } from './SaveVersionDialog';

interface VersionDropdownProps {
  jobId: string;
  activeVersionId?: string | null;
  activeVersionName?: string | null;
  onSelectVersion: (version: {
    id: string;
    name: string;
    createdAt: string;
    snapshot: JobBreakdown;
  }) => void;
  onVersionSaved?: (version: { id: string; name: string }) => void;
  disabled?: boolean;
}

// Format date for display
function formatVersionDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function VersionDropdown({
  jobId,
  activeVersionId,
  activeVersionName,
  onSelectVersion,
  onVersionSaved,
  disabled = false,
}: VersionDropdownProps) {
  const { data: versions, isLoading } = useBreakdownVersions(jobId);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingVersionId, setPendingVersionId] = useState<string | null>(null);

  // Edit/Delete state
  const [editingVersion, setEditingVersion] = useState<Omit<BreakdownVersionWithUser, 'snapshot'> | null>(null);
  const [editName, setEditName] = useState('');
  const [editTag, setEditTag] = useState('');
  const [editNameError, setEditNameError] = useState<string | null>(null);
  const [deleteConfirmVersion, setDeleteConfirmVersion] = useState<Omit<BreakdownVersionWithUser, 'snapshot'> | null>(null);

  const updateVersionMutation = useUpdateBreakdownVersion(jobId);
  const deleteVersionMutation = useDeleteBreakdownVersion(jobId);

  // Fetch the full version when one is selected
  const { data: selectedVersion } = useBreakdownVersion(
    jobId,
    pendingVersionId ?? undefined
  );

  // Use ref to avoid stale closure issues with onSelectVersion callback
  const onSelectVersionRef = useRef(onSelectVersion);
  onSelectVersionRef.current = onSelectVersion;

  // When the selected version loads, trigger the callback.
  // Guard against stale React Query data: only fire when the fetched
  // version's id matches the pending id.
  useEffect(() => {
    if (selectedVersion && pendingVersionId && selectedVersion.id === pendingVersionId) {
      setPendingVersionId(null);
      onSelectVersionRef.current({
        id: selectedVersion.id,
        name: selectedVersion.name || formatVersionDate(selectedVersion.createdAt),
        createdAt: selectedVersion.createdAt,
        snapshot: selectedVersion.snapshot,
      });
    }
  }, [selectedVersion, pendingVersionId]);

  const handleSelectVersion = (version: Omit<BreakdownVersionWithUser, 'snapshot'>) => {
    // Set pending version ID to trigger the fetch
    setPendingVersionId(version.id);
  };

  const handleRenameClick = (e: React.MouseEvent, version: Omit<BreakdownVersionWithUser, 'snapshot'>) => {
    e.stopPropagation();
    setEditingVersion(version);
    setEditName(version.name);
    setEditTag(version.tag ?? '');
    setEditNameError(null);
  };

  const handleDeleteClick = (e: React.MouseEvent, version: Omit<BreakdownVersionWithUser, 'snapshot'>) => {
    e.stopPropagation();
    setDeleteConfirmVersion(version);
  };

  const handleRenameSubmit = () => {
    if (!editingVersion) return;
    const trimmed = editName.trim();
    if (!trimmed) {
      setEditNameError('Snapshot name is required.');
      return;
    }
    setEditNameError(null);
    updateVersionMutation.mutate(
      { versionId: editingVersion.id, name: trimmed, tag: editTag.trim() || null },
      {
        onSuccess: () => setEditingVersion(null),
        onError: (err: Error) => {
          if (err.message?.toLowerCase().includes('already exists')) {
            setEditNameError(err.message);
          }
        },
      }
    );
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmVersion) {
      deleteVersionMutation.mutate(deleteConfirmVersion.id, {
        onSuccess: () => setDeleteConfirmVersion(null),
      });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            disabled={disabled}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <History className="h-4 w-4" />
            {activeVersionName || 'Snapshots'}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuItem onClick={() => setSaveDialogOpen(true)}>
            <Save className="h-4 w-4 mr-2" />
            Save Snapshot...
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {isLoading ? (
            <div className="px-2 py-3 text-sm text-gray-500 text-center">
              Loading snapshots...
            </div>
          ) : !versions || versions.length === 0 ? (
            <div className="px-2 py-3 text-sm text-gray-500 text-center">
              No saved snapshots yet
            </div>
          ) : (
            versions.map((version) => {
              const isActive = version.id === activeVersionId;
              return (
              <div
                key={version.id}
                className={`flex items-center gap-1 px-2 py-2 hover:bg-gray-100 rounded-sm cursor-pointer group ${isActive ? 'bg-gray-50' : ''}`}
                onClick={() => handleSelectVersion(version)}
              >
                {isActive ? (
                  <Check className="h-4 w-4 text-blue-600 shrink-0" />
                ) : (
                  <div className="w-4 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 w-full">
                    <span className={`font-medium truncate flex-1 text-sm ${isActive ? 'text-blue-600' : 'text-gray-900'}`}>
                      {version.name || formatVersionDate(version.createdAt)}
                    </span>
                    <span className="text-xs text-gray-500 tabular-nums">
                      {formatCurrency(version.totalCost)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Clock className="h-3 w-3" />
                    {formatVersionDate(version.createdAt)}
                    {version.tag && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium border border-blue-100">
                        {version.tag}
                      </span>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="p-1 rounded hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreHorizontal className="h-4 w-4 text-gray-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem onClick={(e) => handleRenameClick(e, version)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => handleDeleteClick(e, version)}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              );
            })
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <SaveVersionDialog
        jobId={jobId}
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onVersionSaved={onVersionSaved}
      />

      {/* Rename Dialog */}
      <Dialog open={!!editingVersion} onOpenChange={(open: boolean) => !open && setEditingVersion(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Snapshot</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label htmlFor="snapshot-edit-name" className="text-sm font-medium text-gray-700">
                Snapshot Name
              </label>
              <Input
                id="snapshot-edit-name"
                value={editName}
                onChange={(e) => {
                  setEditName(e.target.value);
                  if (editNameError) setEditNameError(null);
                }}
                placeholder="Enter snapshot name"
                className="mt-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRenameSubmit();
                  }
                }}
              />
              {editNameError && (
                <p className="text-xs text-red-600 mt-1">{editNameError}</p>
              )}
            </div>
            <div>
              <label htmlFor="snapshot-edit-tag" className="text-sm font-medium text-gray-700">
                Tag <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <Input
                id="snapshot-edit-tag"
                value={editTag}
                onChange={(e) => setEditTag(e.target.value)}
                placeholder="e.g., Estimate, Final, Revised"
                maxLength={60}
                className="mt-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRenameSubmit();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingVersion(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleRenameSubmit}
              disabled={!editName.trim() || updateVersionMutation.isPending}
            >
              {updateVersionMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmVersion} onOpenChange={(open: boolean) => !open && setDeleteConfirmVersion(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Snapshot</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirmVersion?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmVersion(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="destructive"
              disabled={deleteVersionMutation.isPending}
            >
              {deleteVersionMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
